import React, { useEffect, useMemo } from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import { CheckboxSelect, OptionType } from '@atlaskit/select';
import { layers } from '@atlaskit/theme';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import SearchField from 'src/components/search-field';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useIntl } from 'src/hooks/intl';
import { BranchingModelState } from 'src/redux/branches/reducers/branch-list-reducer';
import { Ref as BranchRef } from 'src/sections/create-branch';
import { BranchSelector } from 'src/sections/repository/components/ref-selector/branch-selector';
import { Ref } from 'src/sections/repository/types';
import { getIsSiteMessageActive } from 'src/selectors/global-selectors';
import {
  getModalMenuPortalStyles,
  selectControlStyles,
} from 'src/styles/select';

import * as filterStyles from '../../../styles/filters';
import { useRefFetchActions } from '../../branches/hooks/use-ref-fetch-actions';
import AuthorSelector from '../containers/author-selector';
import { PRFilterState } from '../types';

import i18n from './i18n';
import { SortSelect } from './sort-pr-selector';

export type FilterProps = {
  repositoryFullSlug: string;
  isReviewing: boolean;
  search: string;
  uuid: string | undefined;
  currentUserUuid: string;
  branch: string | undefined;
  prState: PRFilterState[];
  prStateAllChecked?: boolean;
  sort: string | undefined;
  onChangeReviewing: (isReviewing: boolean) => void;
  onChangeSearch: (search: string) => void;
  onChangeUuid: (uuid: string | null) => void;
  onChangeBranch: (branch: BranchRef) => void;
  onChangePrState: (
    prStates: PRFilterState[],
    prStateAllChecked?: boolean
  ) => void;
  onChangeSort: (sort: string) => void;
  showIsReviewing: boolean;
  mainBranch?: Ref;
  branchingModel?: BranchingModelState;
};

const PullRequestListFilter = ({
  prState = ['OPEN', 'DRAFT'],
  prStateAllChecked = false,
  ...props
}: FilterProps) => {
  const intl = useIntl();
  const areDraftPRsEnabled = useStatsigGate(StatsigFeatureKeys.bbcDraftPRs);

  const stateOptions = useMemo(() => {
    const states = [
      {
        label: intl.formatMessage(i18n.stateOpen),
        value: 'OPEN',
      },
      {
        label: intl.formatMessage(i18n.stateMerged),
        value: 'MERGED',
      },
      {
        label: intl.formatMessage(i18n.stateDeclined),
        value: 'DECLINED',
      },
    ];
    if (areDraftPRsEnabled) {
      states.push({
        label: intl.formatMessage(i18n.stateDraft),
        value: 'DRAFT',
      });
    }
    return states;
  }, [areDraftPRsEnabled, intl]);

  const prStateValue = useMemo(() => {
    // Handle ALL state specially since it is not an option that can be selected
    if (prState.includes('ALL')) {
      return prStateAllChecked ? stateOptions : [];
    }
    return prState.map(
      state => stateOptions.find(option => option.value === state)!
    );
  }, [prState, prStateAllChecked, stateOptions]);

  const handlePrStateChange = (options: OptionType[]) => {
    const states = options.map(({ value }) => value as PRFilterState);
    if (states.length === 0) {
      props.onChangePrState(['ALL']);
    } else if (states.length === stateOptions.length) {
      props.onChangePrState(['ALL'], true);
    } else {
      props.onChangePrState(states);
    }
  };

  const handleSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
    const search = e.currentTarget.value;
    props.onChangeSearch(search);
  };

  const clearFilter = () => {
    props.onChangeSearch('');
  };

  const { fetchRefs, fetchNextRefs, state: branchState } = useRefFetchActions();

  useEffect(() => {
    fetchRefs();
  }, [fetchRefs]);

  const isBannerOpen = useSelector(getIsSiteMessageActive);
  const myPullRequestsButtonEnabled = useStatsigGate(
    StatsigFeatureKeys.myPullRequestsButtonEnabled
  );

  return (
    <filterStyles.FilterGroup>
      <SearchField
        currentSearch={props.search}
        handleFilterChange={handleSearchChange}
        placeholder={intl.formatMessage(i18n.filterDescription)}
        clearFilter={clearFilter}
        fieldName="search-pull-requests-input"
      />
      <filterStyles.Filter
        mobileOrder={1}
        data-qa="select-pull-request-state"
        isFirstFilterElement
      >
        <CheckboxSelect
          isMulti
          isClearable
          placeholder={intl.formatMessage(i18n.statusPlaceholder)}
          appearance="subtle"
          options={stateOptions}
          isSearchable={false}
          value={prStateValue}
          onChange={handlePrStateChange}
          styles={{
            menu: css => ({
              ...css,
              zIndex: layers.navigation(),
              minWidth: 150,
            }),
          }}
        />
      </filterStyles.Filter>
      <filterStyles.Filter mobileOrder={2}>
        <AuthorSelector
          repositoryFullSlug={props.repositoryFullSlug}
          uuid={props.uuid}
          onChange={props.onChangeUuid}
          prStatus={prState}
        />
      </filterStyles.Filter>
      <filterStyles.Filter mobileOrder={1}>
        <BranchSelector
          branchSelector={branchState}
          fetchRefOptions={fetchRefs}
          fetchNextRefOptions={fetchNextRefs}
          onChangeBranch={props.onChangeBranch}
          value={
            props.branch
              ? { label: props.branch, value: props.branch }
              : undefined
          }
          isClearable
          placeholder={intl.formatMessage(i18n.filterBranch)}
          styleOverride={{
            ...getModalMenuPortalStyles(isBannerOpen, '300px'),
            control: (css: any, state: any) => ({
              ...css,
              ...filterStyles.subtleSelector(css, state),
              ...selectControlStyles,
              minWidth: '160px',
            }),
            placeholder: (css: any) => ({
              ...css,
              ...filterStyles.nothingSelectedColor(),
            }),
          }}
        />
      </filterStyles.Filter>

      {myPullRequestsButtonEnabled ? (
        <filterStyles.Filter data-qa="my-pull-requests-button" mobileOrder={2}>
          <CustomThemeButton
            appearance="subtle"
            aria-pressed={props.uuid === props.currentUserUuid}
            isSelected={props.uuid === props.currentUserUuid}
            onClick={() =>
              props.onChangeUuid(
                props.uuid !== props.currentUserUuid // If current user is not selected, they should be selected. Else, clear the value.
                  ? props.currentUserUuid
                  : null
              )
            }
          >
            <FormattedMessage {...i18n.authorPullRequests} />
          </CustomThemeButton>
        </filterStyles.Filter>
      ) : null}
      {props.showIsReviewing && (
        <filterStyles.Filter data-qa="im-reviewing-button" mobileOrder={2}>
          <CustomThemeButton
            appearance="subtle"
            aria-pressed={props.isReviewing}
            isSelected={props.isReviewing}
            onClick={() => props.onChangeReviewing(!props.isReviewing)}
          >
            <FormattedMessage {...i18n.userFilterReviewing} />
          </CustomThemeButton>
        </filterStyles.Filter>
      )}
      <filterStyles.SortSelectorWrapper>
        <filterStyles.Filter mobileOrder={1} data-qa="select-pull-request-sort">
          <SortSelect sort={props.sort} onChangeSort={props.onChangeSort} />
        </filterStyles.Filter>
      </filterStyles.SortSelectorWrapper>
    </filterStyles.FilterGroup>
  );
};

export default PullRequestListFilter;
